/**
 * Copyright (c) 2020-2021 Perficient, Inc.
 * Unauthorized copying or distribution of this file prohibited without the express permission of Perficient.
 */

import axios from "axios";
import { APIGateways } from "../../../utility/APIGateways";
import { getAuthHeader } from "../../../utility/auth";
import { v2, errorToObject } from "@prftesp/cle-logger";
import errorCodes from "../../../utility/errorCodes";

const COMPONENT_NAME = "Admin webChatManager/services/TranslateService";

/**
 * This service is for calling a lambda to update the terminology file in AWS translate.
 * @param {Object} updateCSVObject the object to be sent to update.
 */
export const updateTerminologyFileAsync = async (updateCSVObject) => {
	try {
		let updateResponse = await axios.post(`${APIGateways.webChatTranslationTerminologyEndpoint}`, updateCSVObject, {
			headers: await getAuthHeader(),
		});
		return updateResponse.data;
	} catch (error) {
		v2.error(`Error updating web chat terminology`, {
			additionalData: { error: errorToObject(error), data: { updateCSVObject } },
			componentName: COMPONENT_NAME,
			logCode: errorCodes.UPDATE_TERMINOLOGIES_API_ERROR,
		});
		throw error;
	}
};

/**
 * This service retrieves the terminology data from AWS translate so that the file can be retrieved.
 * @param {String} fileName The name of the file to retrieve (without '.csv')
 */
export const getTerminologyFileDataAsync = async (fileName) => {
	try {
		let response = await axios.get(`${APIGateways.webChatTranslationTerminologyEndpoint}/${fileName}`, {
			headers: await getAuthHeader(),
		});
		return response?.data?.result;
	} catch (error) {
		v2.error(`Error getting web chat terminology`, {
			additionalData: { error: errorToObject(error), data: { fileName } },
			componentName: COMPONENT_NAME,
			logCode: errorCodes.GET_WEBCHAT_TERMINOLOGIES_API_ERROR,
		});
		throw error;
	}
};

/**
 * This service deletes the terminology data from AWS translate so that the file can be retrieved.
 * @param {String} fileName The name of the file to retrieve (without '.csv')
 */
export const deleteTerminologyFileAsync = async () => {
	try {
		let response = await axios.delete(`${APIGateways.webChatTranslationTerminologyEndpoint}`, {
			headers: await getAuthHeader(),
		});
		return { success: true, data: response?.data?.result };
	} catch (error) {
		v2.error(`Error deleting web chat terminology`, {
			additionalData: { error: errorToObject(error) },
			componentName: COMPONENT_NAME,
			logCode: errorCodes.DELETE_TERMINOLOGY_DATA_API_ERROR,
		});
		return { success: false, error: error?.response?.data?.error };
	}
};
